import React, { useState, useEffect } from 'react';

export default function ProductShowcaseModal({ product, onClose }) {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    // ESC key support
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleClose = () => {
    onClose();
  };

  const handleOverlayClick = (e) => {
    // Only close if clicking directly on the overlay
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 2500);
  };

  const handleShowcaseLinkClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    showToastMessage(`🔗 Link showcased: "${product.name}" — No external navigation.`);
  };

  const handleAnyLinkClick = (e) => {
    const targetAnchor = e.target.closest('a');
    if (targetAnchor) {
      const href = targetAnchor.getAttribute('href');
      if (href && href !== 'javascript:void(0);' && !href.startsWith('#') && !href.startsWith('javascript:')) {
        e.preventDefault();
        e.stopPropagation();
        showToastMessage(`🚫 Navigation prevented. Link stays showcased inside modal.`);
      }
    }
  };

  // Function to get random page number between 4 and 16
  const getRandomPage = () => {
    return Math.floor(Math.random() * 13) + 4; // 4-16 range
  };

  // Function to navigate to specific page in the Canva embed
  const navigateToPage = (pageNumber) => {
    const iframe = document.querySelector('iframe');
    if (iframe) {
      const newSrc = `${showcaseUrl}#${pageNumber}`;
      iframe.src = newSrc;
    }
  };

  // Get random page on component mount
  const targetPage = getRandomPage();

  // Sample showcase URL - in real implementation, this would come from product data
  const showcaseUrl = product.showcaseUrl || "https://www.canva.com/design/DAHGxTtuoF8/Ueu3Bx-jFLReDDmU6cdy3Q/view?embed";

  useEffect(() => {
    navigateToPage(targetPage);
  }, [targetPage, showcaseUrl]);

  useEffect(() => {
    navigateToPage(targetPage);
  }, [targetPage, showcaseUrl]);

  return (
    <>
      {/* Modal Overlay */}
      <div 
        className="fixed inset-0 bg-[rgba(20,30,18,0.92)] backdrop-blur-sm z-[2000] transition-opacity duration-200"
        onClick={handleOverlayClick}
      >
        {/* Modal Container */}
        <div className="absolute inset-0 w-full h-full bg-white shadow-[0_40px_70px_rgba(0,0,0,0.4)] flex flex-col overflow-hidden animate-[modalFloatIn_0.25s_cubic-bezier(0.2,0.9,0.4,1.1)]">
          
          {/* Modal Header */}
          <div className="flex justify-between items-center p-3 sm:p-4 bg-[#fefef7] border-b border-[#e2efe0]">
            <h3 className="text-lg sm:text-xl font-semibold text-[#033EA5] truncate">
              {product.name || 'Product Showcase'}
            </h3>
            <button
              onClick={handleClose}
              className="bg-[#f0f2ee] border-none text-lg sm:text-xl leading-none cursor-pointer w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-all duration-200 text-[#2b5a36] font-medium hover:bg-[#e2e8de] hover:scale-105 hover:text-black"
              aria-label="Close modal"
            >
              ✕
            </button>
          </div>

          {/* Modal Body - Full Screen Canva Embed */}
          <div 
            className="flex-1 relative bg-[#fafef5] overflow-hidden"
            onClick={handleAnyLinkClick}
          >
            {/* Embed Wrapper - Takes full available space */}
            <div className="absolute inset-0 bg-[#eef2e8] will-change-transform">
              <iframe
                loading="lazy"
                className="absolute inset-4 w-full h-full border-none rounded-[8px] sm:rounded-[12px] md:rounded-[16px]"
                src={`${showcaseUrl}#${targetPage}`}
                allowFullScreen={true}
                allow="fullscreen"
                title={`${product.name || 'Product'} - Showcase`}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Toast Message */}
      {showToast && (
        <div className="fixed bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 scale-90 bg-[#1e2b22e6] backdrop-blur-md text-[#f2f7ea] px-3 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium z-[2100] transition-all duration-200 opacity-100 scale-100 max-w-[80vw] sm:max-w-none text-center font-mono tracking-wide">
          {toastMessage}
        </div>
      )}

      {/* CSS for modal animation */}
      <style jsx>{`
        @keyframes modalFloatIn {
          0% {
            transform: scale(0.96);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}
